import {i18n} from '../components/app/AppSetup'
import Numeral from 'numeral'
import { Snackbar } from '../components/common/Snackbar'
import UserSessionService from "./userSessionService"

const FormatService = {

    booleanYesNoFormatter(params) {
        //for general use
        if (typeof params === 'boolean') {
            return params ? i18n.global.t('YES') : i18n.global.t('NO')
        //for ag-grid columns
        } else if (typeof params !== 'boolean' && typeof params.value === 'boolean') {
            return params.value ? i18n.global.t('YES') : i18n.global.t('NO')
        } else {
            return null
        }
    },
    booleanOnOffFormatter(params) {
        if (!params || params.value === undefined || params.value === null) {
            return i18n.global.t('Unknown')
        }

        return params.value ? i18n.global.t('On') : i18n.global.t('Off')
    },
    doubleFormatter(params) {
        //for general use
        if (params && typeof params === 'number') {
            return Numeral(params).format('0,0.00')
        //for ag-grid columns
        } else if (params && typeof params !== 'number' && typeof params.value === 'number') {
            const fmt = params.value.fmt || '0,0.00'
            return Numeral(params.value).format(fmt)
        } else if (params && typeof params !== 'number' && typeof params?.value !== 'number') {
            return null
        }
    },

    latLongFormatter(params) {
        //for ag-grid columns
        let fmt = '0.[000000]'
        if (params?.data?.location?.gps?.latitude && params?.data?.location?.gps?.longitude) {
            return Numeral(params.data.location.gps.latitude).format(fmt) + ' / ' + Numeral(params.data.location.gps.longitude).format(fmt)
        } else {
            return null
        }
    },

    listFormatter(params){
        //for general use
        if (Array.isArray(params) && params.length > 0) {
            return params.join(", ")
        //for ag-grid columns
        } else if (Array.isArray(params.value)) {
            return params.value.join(", ")
        } else {
            return null
        }
    },

    numberFormatter(params, format = '0,0')
    {
        if (params == null)
        {
            return null
        }
        //for general use
        if (typeof params === 'number')
        {
            return Numeral(params).format(format)
        }
        //for ag-grid columns
        else if (typeof params !== 'number' && typeof params.value === 'number')
        {
            const fmt = params.colDef?.context?.fmt || format
            return Numeral(params.value).format(fmt)
        }
        else
        {
            return params.colDef?.context?.showNotApplicable ? i18n.global.t('common.notApplicable') : null
        }
    },

    // Uplink route tuning stats only exist for base station routes (destType 0), so buddy routes read N/A rather than blank
    routesTuningStatFormatter(params)
    {
        if (params?.data && params.data.destType !== 0)
        {
            return i18n.global.t('common.notApplicable')
        }
        return FormatService.numberFormatter(params)
    },

    // Convert milliseconds to seconds and nicely format the numeric value into a readable form.
    // Originally lived on the removed globalMixin (nmMillisToSecs) prior to the Vue 2 -> Vue 3 migration.
    millisToSecsFormatter(params)
    {
        if (!params || params.value === undefined || params.value === null)
        {
            return null
        }
        const secs = params.value / 1000
        return FormatService.numberFormatter(secs)
    },

    percentageFormatter(params, showNotApplicable = true, format = '0.00%') {
        //for general use
        if (typeof params === 'number') {
            return Numeral(params).format(format)
        //for ag-grid columns
        } else if (typeof params !== 'number' && typeof params.value === 'number') {
            const fmt = params.fmt || format
            return Numeral(params.value).format(fmt)
        } else if (typeof params !== 'number' && typeof params.value !== 'number') {
            return showNotApplicable ? i18n.global.t('common.notApplicable') : null
        }
    },
    
    sortedListFormatter(params) {
        //for ag-grid columns
        if (!Array.isArray(params.value) || !params.value.length) {
            return null
        }

        return FormatService.listFormatter({value: params.value.sort()})
    },

    //TODO finish rewriting the rest of these

    sampleRateFormatter(params) {
        //for general use
        if (params === null) {
            return i18n.global.t('common.notset')
        }

        let widthVal = Number(params)
        if (isNaN(widthVal)) {
            return i18n.global.t('common.unknown')
        } else if (widthVal >= 1440) {
            return (widthVal / 1440) + " " + i18n.global.t('common.days')
        } else if (widthVal >= 60) {
            return (widthVal / 60) + " " + i18n.global.t('common.hours')
        } else {
            return widthVal + " " + i18n.global.t('common.minutes')
        }
    },
    nmFetchDataError(msg = null) {
        if (!msg) {
            msg = i18n.global.t("common.fetchDataError");
        }
        Snackbar().show({msg: msg, color:"error"})
    },

    nmTranslatorRenderer(params) {
        if (!params || params.value === undefined || params.value === null)
        {
            return null
        }
        let keyStr = params.prefix + params.value;
        let i18KeyStr = i18n.global.t(keyStr, {...params.keys});
        if (i18KeyStr === keyStr)
        {
            i18KeyStr = i18n.global.t(params.prefix + "unknown") + " - " + params.value;
        }
        return (i18KeyStr);
    },

    nmRadioStateFormatter(params) {
        if (!params || params.value === undefined || params.value === null) {
            return ''
        }
        return (i18n.global.t('radio.topLevelStateNames.' + params.value))
    },
    nmMinutesDuration(value) {
            const MINUTES_PER_DAY = 1440
            const MINUTES_PER_HOUR = 60
            const SECONDS_PER_MINUTE = 60

            if (value !== undefined && value !== null) {
                let duration = ''
                let minutes

                // Handle value parameter being a scalar or an object with a value property, so that it can be used in table formatting
                if (typeof value === 'object') {
                    minutes = value.value // e.g. params.value
                } else {
                    minutes = value
                }
                if (minutes < 1)
                {
                    return ((minutes*SECONDS_PER_MINUTE) + i18n.global.t('common.secondAbbrev'))
                }

                let days = Math.floor(minutes / MINUTES_PER_DAY)
                minutes = minutes % MINUTES_PER_DAY

                let hours = Math.floor(minutes / MINUTES_PER_HOUR)
                //javascript floating point math does not guarantee integer for % operator, so we will round if !== 0, omit if 0
                //we are omitting 0 to show tuning thresholds as 1day instead of 1day 0min, etc. it's a design choice.
                minutes = minutes % MINUTES_PER_HOUR ? (minutes % MINUTES_PER_HOUR).toFixed(0) : minutes % MINUTES_PER_HOUR

                if (days) {
                    duration = days.toLocaleString() + i18n.global.t('common.dayAbbrev') + ' '
                }
                if (hours) {
                    duration += hours + i18n.global.t('common.hourAbbrev') + ' '
                }

                if (minutes || !duration) {
                    duration += minutes + i18n.global.t('common.minuteAbbrev')
                }
                return duration
            }
            return null
        },

    /**
     * Convert the transmit interval from secs to a readable string
     * @param value  transmit interval in secsonds
     * @returns {string}
     */
    nmTransmitIntervalFilter(value, shorthand = false)
    {
        if (!value && value !== 0)
        {
            return ('')
        }
        let widthVal = Number (value)
        if (isNaN(widthVal))
        {
            return ('')
        }
        if (widthVal === 0) {
            widthVal = i18n.global.t('radio.factorySleep')
        }
        else if (widthVal === -1)  //Special case for n/a
        {
            widthVal = i18n.global.t('common.notApplicable')
        }
        else if (widthVal >= 3600)
        {
            const hourLabel = shorthand ? i18n.global.t('common.hourAbbrev') : " " + i18n.global.t('common.hours')
            widthVal = (widthVal / 3600) + hourLabel
        }
        else if (widthVal >= 60)
        {
            const minLabel = shorthand ? i18n.global.t('common.minuteAbbrev') : " " + i18n.global.t('common.minutes')
            widthVal = (widthVal / 60) + minLabel
        }
        else
        {
            widthVal = widthVal + " " + i18n.global.t('common.seconds')
        }
        return (widthVal)
    },
    getOptionListFromI18nFile(key, allowNull = true, allowUnknown = true) {
                let listItems = i18n.global.tm(key);
                let options = []
                for (let item in listItems) {
                    if ((item === 'null') && (!allowNull)) {
                        ;  // Do not add to list if null and we don't want nulls
                    }
                    else if ((item === 'UNKNOWN') && (!allowUnknown)) {
                        ;  // Do not add to list if unknown and we don't want unknown
                    }
                    else {
                        options.push({ value: item, name: listItems[item] })
                    }
                }
                return (options)
            },
    nmTransmitIntervalRenderer(rowObj, shorthand = false)
    {
        let value = rowObj;
        if (typeof value === 'object') {
            value = rowObj.value
        }
        return (FormatService.nmTransmitIntervalFilter (value, shorthand))
    },

    nmRadioModulationFormatter(params) {
        //if (!params || params.value === undefined || params.value === null) {
        if (!params?.value)
        {
            return ''
        }
        if (Array.isArray(params.value))
        {
            return (FormatService.nmArrayTranslatorRenderer({prefix: 'radio.modulations.', value: params.value}))
        }
        return (i18n.global.t('radio.modulations.' + params.value))
    },

    nmRadioFECFormatter(params) {
        if (!params || params.value === undefined || params.value === null) {
            return ''
        }
        return (i18n.global.t('fec.' + params.value))
    },

    translateModDemodFec (keys, filter)
    {
        let value = keys.modulationDemodulation;
        let name = i18n.global.t('modulation.' + value)
        if (name === 'modulation.' + value) {
            name = i18n.global.t('tower.demodulatorTypes.' + value)
        }
        name += ' ' + i18n.global.t('fec.' + keys.fec)
        if (!filter || (!filter.frequency))
        {
            name = FormatService.nmFrequencyNameFormatter(keys) + " - " + name;
        }
        return name
    },
    translateCustomer (keys)
    {
        let customer = keys.customer;
        if (customer === 'other')
        {
            return i18n.global.t('customer.other')
        }
        else
        {
            let user = UserSessionService.getCurrentUser()
            let userCustomer = (user.customers || []).find(e => e.name === keys.customer)
            if (userCustomer)
            {
                return userCustomer.displayName || userCustomer.name
            }
            else
            {
                return keys.customer
            }
        }
    },

    nmArrayTranslatorRenderer(params) {
        let arrayList
        if (Array.isArray(params))
        {
            arrayList = params
        }
        else if (!params?.value)
        {
            return (null)
        }
        else
        {
            arrayList = params.value

        }
        let list = ''
        arrayList.forEach(item => {
            if (list.length) {
                list += ', '
            }
            if (params.prefix)
            {
                list += FormatService.nmTranslatorRenderer({prefix: params.prefix, value: item})
            }
            else
            {
                list += item
            }
        })
        return list
    },    

    nmReadMinutesRenderer(rowObj) {
        let value = rowObj;
        if (typeof value === 'object') {
            value = rowObj.value
        }
        
        //let str = FormatService.$options.filters.nmReadMinutesFilter(value, this)
        let str = FormatService.nmMinutesDuration(value)

        //don't append raw value if str DNE or if str is already only in minutes
        if (str && (str.includes(i18n.global.t('common.dayAbbrev')) || str.includes(i18n.global.t('common.hourAbbrev')))) {

            if (str.includes(i18n.global.t('common.minuteAbbrev'))) {
                str += ' '
            }

            str += "(" + value + " " + i18n.global.t('common.minuteAbbrev') + ")"
        }
        
        return str
    },

    nmConvertArrayToObject(array, key)
    {
        if (!array || !array.length)
        {
            return {}
        }
        else if (!key)
        {
            return array.reduce((obj, item) => {
                obj[item] = item
                return obj
            }, {})
        }
        return {}
    },
    /**
     * Format the frequency data in a nice format of Name (Freq MHz)  Input format is an object containing
     * { frequency, frequencyName}
     * @param params
     * @returns {string|string}
     **/
    nmFrequencyNameFormatter(params)
    {
        // Make sure our frequency is a number so later math does not blow up
        if (!params)
        {
            return ''
        }
        // If we get called from a grid cell, then params will be an object with a data property
        let freqObj = params
        if (params.data)
        {
            freqObj = params.data
        }
        let frequency = 0
        let freqName = freqObj.frequencyName

        // there will not be a frequency name if the frequency is null
        // if there is no frequency name then it will be the same value as the frequency

        // if frequency is not a number or set to null then return 'Not Set'
        if (freqObj.frequency === null || isNaN(freqObj.frequency))
        {
            return i18n.global.t('common.notset')
        }
        else
        {
            frequency = freqObj.frequency
        }
        // Check to see we have a name and the name is different from the frequency so we dont duplicate the info
        if (freqName && freqName !== 'null' && freqName.toString() !== frequency.toString())
        {
            // Return a string Name (MHz)
            return (freqName + " (" + Number.parseFloat (frequency / 1000000).toFixed(5) + ")")
        }
        // Since there is no name or name == freq, then just return the freq converted to MHz
        return (Number.parseFloat (frequency / 1000000).toFixed(5))
    },
    nmChannelFormatter(channelObj, includeModulations = true) {
        if (!channelObj) {
            return ''
        } else {
            let freqName = FormatService.nmFrequencyNameFormatter(channelObj)
            let modName = ''
            if ((includeModulations) && (channelObj.modulation))
            {
                modName = " (" + FormatService.nmRadioModulationFormatter({value: channelObj.modulation}) + ")"
            } else if ((includeModulations) && (channelObj.modulations))
            {
                modName = " (" + channelObj.modulations.join(', ') + ")"
            }
            return (freqName + modName)
        }
    },
    translateModDemod (keys, filter)
    {
        let value = keys.modulationDemodulation;
        let name = i18n.global.t('modulation.' + value)
        if (name === 'modulation.' + value)
        {
            name = i18n.global.t('tower.demodulatorTypes.' + value)
        }
        if (!filter || (!filter.frequency))
        {
            name = FormatService.nmFrequencyNameFormatter(keys) + " - " + name;
        }
        return name
    },
    nmNAFormatNumber(value)
    {
        let formattedNum = FormatService.nmFormatNumber(value)
        if (formattedNum === null || formattedNum === 'NaN')
        {
            return i18n.global.t('common.notApplicable')
        }
        return (formattedNum)
    },
    nmFormatDouble(value)
    {
        return (FormatService.nmFormatNumber(value, '0,0.00'))
    },
    nmFormatNumber(value, fmt = '0,0')
    {
        let numValue = value
        if (numValue == null)
        {
            return null
        }
        if (typeof value === 'object')
        {
            numValue = value.value
        }
        if (isNaN(numValue))
        {
            return null
        }
        let formattedNum = Numeral(numValue).format(fmt)
        return formattedNum === 'NaN' ? null : formattedNum
    },
    /**
     * Format a number as a percentage string
     * @param params {value, fmt}, value is the number to be formatted, fmt string is how to format the value, fmt is
     * optional, if left out, then the value is mulitplied * 100 and formmatted with 2 decimal places
     * @returns {string|VueI18n.TranslateResult|TranslateResult|*}
     */
    nmNumberPercentageFormatter(params, showNotApplicable = true)
    {
        if (params?.value == null)
        {
            return showNotApplicable ? i18n.global.t('common.notApplicable') : ''
        }

        let fmtStr = '0.00 %'
        if (params.fmt)
        {
            fmtStr = params.fmt
        }
        // A format with % in it will perform the multiplication inside Numeral
        if (fmtStr.includes("%"))
        {
            return Numeral(params.value).format(fmtStr)
        }
        return Numeral(params.value).format(fmtStr) + "%"
    },

    nmTitleCase(value)
    {
        if (!value)
        {
            return value
        }
        return value.charAt(0).toUpperCase() + value.slice(1).toLowerCase()
    },
    nmLatLongFormatter(params)
    {
        let fmt = '0.[000000]'
        if (params?.data?.location?.gps?.latitude && params?.data?.location?.gps?.longitude)
        {
            return Numeral(params.data.location.gps.latitude).format(fmt) + ' / ' + Numeral(params.data.location.gps.longitude).format(fmt)
        }
        else
        {
            return null
        }
    },
    nmBooleanYesNoFormatter(params)
    {
        let value = params
        if (typeof value !== "boolean")
        {
            value = params?.value
        }
        if (value === null || value === undefined)
        {
            return i18n.global.t('common.notset')
        }
        return value ? i18n.global.t('YES') : i18n.global.t('NO')
    },
    /**
     * Get the config values from the i18n objects
     * @param i18Key the i18n key to use to lookup
     * @returns {[]}
     */
    getFilterConfigValues(i18Key)
    {
        let ary = []
        // Note tm will only handle arrays or objects, t only handles strings
        let names = i18n.global.tm(i18Key)
        if (Object.keys(names).length === 0)
        {
            console.warn ("No i18n data found for key", i18Key)
            // if no data found in i18n file then its returns back same i18nKey, so compare return value and 18nKey if same return nothing
            // this condition added specially for netTuning filter values
            return ary // TODO:  This breaks tuning as implemented, need to fix in tuning
        }
        for (let key in names)
        {
            if (names.hasOwnProperty(key))
            {
                // Convert a null string to a null value for proper filtering
                if (key === "null")
                {
                    ary.push({name: i18n.global.t("common.notset"), id: null})
                }
                else
                {
                    ary.push({name: names[key], id: key})
                }
            }
        }
        return (ary)
    },
    nmListFormatter(params)
    {
        if (params?.value == null)
        {
            return null
        }
        let list = ''
        params.value.forEach(item => {
            if (list.length)
            {
                list += ', '
            }
            list += item
        })
        return list
    },
    nmSortedListFormatter(params)
    {
        if (params?.value == null || !Array.isArray(params.value) || (params.value.length === 0))
        {
            return null
        }
        return (FormatService.nmListFormatter ({value: params.value.sort()}))
    },
    // Format the active or standby image obj info into a nice string to display
    activeStandbyImageStrFormatter(data, isActiveBank=false)
    {
        let formattedStr = ''
        if (!data.data)
        {
            return ''
        }
        if (isActiveBank && data.data?.software?.activeBank)
        {
            formattedStr = FormatService.formatImageInfo(data.data.software.activeBank)
            return formattedStr
        }
        const banks = data.data?.software?.banks
        if (banks?.length > 0)
        {
            banks.forEach(item => {
                if (!item.active) {
                    formattedStr = formattedStr + " " + FormatService.formatImageInfo(item)
                }
            })
        }
        if (!formattedStr)
        {
            formattedStr = i18n.global.t('common.notset')
        }
        return formattedStr
    },
    formatImageInfo(obj)
    {
        return  obj?.rootfs?.version
    },
    /**
     * Convert the a modulation and fec aggregation to a readable string
     * @param value  the first stat name
     * @param value  the second stat name
     * @returns {string}
     */
    nmModulationFECFormatter (value, value2)
    {
        let val1String = i18n.global.t('common.notset');
        let val2String = i18n.global.t('common.notset');

        if (value)
        {
            val1String = (i18n.global.t('radio.modulations.' + value))
        }
        if (value2)
        {
            val2String = (i18n.global.t('fec.' + value2));
        }

        return val1String + "; " + val2String
    },
    nmSingleLineAddress(streetAddress)
    {
        if (!streetAddress)
        {
            return ''
        }
        return (streetAddress.address1 || '') + ' ' +
            (streetAddress.address2 || '') + ' ' +
            (streetAddress.city || '') + ' ' +
            (streetAddress.state || '') + ' ' +
            (streetAddress.zipCode || '')

    },
    nmWidthFormatter (params)
    {
        // NOSONAR, this is allow and in fact, needed in javascript
        if (!params || isNaN(params.value))
        {
            return ('')
        }
        return (params.value / 1000)
    },
    nmLatLongEstFormatter(params)
    {
        let fmt = '0.[000000]'
        if (params.data?.location?.gps?.latitudeCalc && params.data?.location?.gps?.longitudeCalc)
        {
            return Numeral(params.data.location.gps.latitudeCalc).format(fmt) + ' / ' + Numeral(params.data.location.gps.longitudeCalc).format(fmt)
        }
        else
        {
            return null
        }
    },
    nmFrequencyFormatter(params) {
        if (!params || !params.value) {
            return "";
        } else {
            if (!isNaN(params.value)) {
                return Number.parseFloat(params.value / 1000000).toFixed(6);
            } else {
                return params.value;
            }
        }
    },
    nmCountArrayList (params)
    {
        if (params.value)
        {
            return params.value.length
        }
        return 0
    },
    formatFileSize(bytes) {
        if(bytes == 0) return '0 Bytes';
        let k = 1024, dm = 2, sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
        let i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
    },
}
export default FormatService
